import React, { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { ShieldCheck, LogOut, FileDown, Mail, AlertTriangle, ListChecks, Gavel, Flag, TrendingUp, Plus, Send, Network, Award, FileText, Download, CheckCircle2 } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import EvidenceGraph from "../components/admin/EvidenceGraph";

const BASE = process.env.REACT_APP_BACKEND_URL;
const ragCls = { red: "text-red-700 bg-red-50 border-red-200", amber: "text-amber-700 bg-amber-50 border-amber-200", green: "text-emerald-700 bg-emerald-50 border-emerald-200" };
const pill = (rag, label) => <span className={`text-[12px] rounded-full px-3 py-1 border font-semibold ${ragCls[rag] || ragCls.green}`}>● {label}</span>;
const Rag = ({ rag }) => <span className={`text-[11px] rounded px-1.5 py-0.5 border font-semibold ${ragCls[rag] || ragCls.green}`}>{(rag || "green")[0].toUpperCase()}{(rag || "green").slice(1)}</span>;
const th = "text-left text-[10px] tracking-[0.12em] uppercase text-zinc-400 font-medium px-3 py-2";
const td = "px-3 py-2 text-[13px] text-zinc-700 align-top";

function SignIn() {
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [preview, setPreview] = useState("");
  const [busy, setBusy] = useState(false);
  const err = new URLSearchParams(loc.search).get("error");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/portal/login/start", { email });
      setSent(true);
      setPreview(data.preview_link || "");
    } catch { setSent(true); }
    finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50 px-4">
      <div className="w-full max-w-md bg-white border border-zinc-200 rounded-xl p-8" data-testid="portal-signin">
        <div className="flex items-center gap-2 text-[var(--e360-primary,#0B5FFF)] mb-1"><ShieldCheck size={18} /><span className="text-[11px] tracking-[0.18em] uppercase">EKSELENS360 · Client Portal</span></div>
        <h1 className="text-2xl font-semibold text-zinc-900 mb-2">Sign in to your engagement</h1>
        {err && <div className="text-[12px] text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2 mb-3" data-testid="portal-signin-error">We couldn't sign you in ({err}). Please request a new link.</div>}
        {sent ? (
          <div data-testid="portal-signin-sent" className="space-y-3">
            <p className="text-[14px] text-zinc-600 flex items-start gap-2"><Mail size={18} className="mt-0.5 text-emerald-600" /> If your email is registered, a secure sign-in link is on its way. Open it in this browser.</p>
            {preview && <a data-testid="portal-preview-link" href={`${BASE}${preview}`} className="inline-block text-[12px] tracking-[0.1em] uppercase bg-zinc-900 text-white px-4 py-2 rounded">Preview: open sign-in link</a>}
            <button onClick={() => { setSent(false); setPreview(""); }} className="block text-[12px] text-zinc-500 underline">Use a different email</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <p className="text-[14px] text-zinc-600">Enter the email your engagement team registered. We'll email you a one-time secure link.</p>
            <input data-testid="portal-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="name@yourcompany.com" className="w-full border border-zinc-300 rounded-lg px-3 py-2.5 text-[15px]" />
            <button data-testid="portal-signin-submit" disabled={busy} type="submit"
              className="w-full bg-[var(--e360-primary,#0B5FFF)] text-white rounded-lg py-2.5 text-[13px] tracking-[0.12em] uppercase disabled:opacity-50">
              {busy ? "Sending…" : "Send secure sign-in link"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

const overdue = (d, statuses, st) => d && statuses.includes(st) && new Date(d) < new Date(new Date().toDateString());

const win = "border border-zinc-300 rounded px-2 py-1 text-[12px] bg-white";

function RaiseRiskForm({ clientId, onChange }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ category: "Risk", item: "", area: "", impact: "Medium" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.item.trim()) { toast.error("Describe the risk"); return; }
    setBusy(true);
    try { await api.post(`/portal/engagement/${clientId}/raid`, f); toast.success("Raised with your engagement team"); setF({ category: "Risk", item: "", area: "", impact: "Medium" }); setOpen(false); onChange?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  if (!open) return <button data-testid="portal-raise-risk-toggle" onClick={() => setOpen(true)} className="inline-flex items-center gap-1.5 text-[12px] tracking-[0.1em] uppercase text-[var(--e360-primary,#0B5FFF)] border border-[var(--e360-primary,#0B5FFF)] px-3 py-1.5 rounded"><Plus size={13} /> Raise a risk</button>;
  return (
    <div data-testid="portal-raise-risk-form" className="border border-zinc-200 rounded-lg p-3 bg-zinc-50 space-y-2">
      <div className="grid sm:grid-cols-3 gap-2">
        <select data-testid="portal-risk-category" className={win} value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })}>
          {["Risk", "Issue", "Dependency"].map((c) => <option key={c}>{c}</option>)}
        </select>
        <select data-testid="portal-risk-impact" className={win} value={f.impact} onChange={(e) => setF({ ...f, impact: e.target.value })}>
          {["High", "Medium", "Low"].map((c) => <option key={c} value={c}>Impact: {c}</option>)}
        </select>
        <input data-testid="portal-risk-area" className={win} placeholder="Area (optional)" value={f.area} onChange={(e) => setF({ ...f, area: e.target.value })} />
      </div>
      <input data-testid="portal-risk-item" className={`${win} w-full`} placeholder="What is the risk / issue / dependency?" value={f.item} onChange={(e) => setF({ ...f, item: e.target.value })} />
      <div className="flex gap-2">
        <button data-testid="portal-risk-submit" disabled={busy} onClick={submit} className="text-[12px] tracking-[0.1em] uppercase bg-[var(--e360-primary,#0B5FFF)] text-white px-4 py-1.5 rounded disabled:opacity-40">Submit</button>
        <button onClick={() => setOpen(false)} className="text-[12px] tracking-[0.1em] uppercase text-zinc-500 px-3 py-1.5">Cancel</button>
      </div>
    </div>
  );
}

function RaidView({ data, clientId, onChange }) {
  const items = (data.items || []).filter((i) => ["Open", "In progress", "Mitigating"].includes(i.status));
  const s = data.summary || {};
  return (
    <div data-testid="portal-raid">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        {pill(s.overall, `RAID — ${s.open || 0} open · ${s.overdue || 0} overdue`)}
        <RaiseRiskForm clientId={clientId} onChange={onChange} />
      </div>
      {items.length === 0 ? <p className="text-[13px] text-zinc-500 py-4">No open items.</p> : (
        <div className="border border-zinc-200 rounded-lg overflow-hidden"><table className="w-full">
          <thead className="bg-zinc-50"><tr><th className={th}>Ref</th><th className={th}>Type</th><th className={th}>Item</th><th className={th}>Owner</th><th className={th}>RAG</th><th className={th}>Status</th></tr></thead>
          <tbody className="divide-y divide-zinc-100">{items.map((i) => (
            <tr key={i.id} className={overdue(i.due_date, ["Open", "In progress", "Mitigating"], i.status) ? "bg-red-50/40" : ""}>
              <td className={`${td} font-mono text-zinc-400`}>{i.ref}{i.raised_by_client && <span className="ml-1 text-[9px] bg-blue-50 text-blue-600 rounded px-1 py-0.5 align-middle">you</span>}</td><td className={td}>{i.category}</td><td className={td}>{i.item}</td>
              <td className={td}>{i.owner}</td><td className={td}><Rag rag={i.rag} /></td><td className={td}>{i.status}</td></tr>))}
          </tbody></table></div>
      )}
    </div>
  );
}

function DecisionRespond({ clientId, itemId, onChange }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!note.trim()) return;
    setBusy(true);
    try { await api.post(`/portal/engagement/${clientId}/decisions/${itemId}/response`, { note }); toast.success("Response sent"); setNote(""); onChange?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-1.5 mt-1">
      <input data-testid={`portal-decision-response-${itemId}`} className={`${win} flex-1`} placeholder="Add your response for the team…" value={note} onChange={(e) => setNote(e.target.value)} />
      <button data-testid={`portal-decision-respond-${itemId}`} disabled={busy || !note.trim()} onClick={submit} className="inline-flex items-center gap-1 text-[11px] tracking-[0.1em] uppercase text-[var(--e360-primary,#0B5FFF)] border border-[var(--e360-primary,#0B5FFF)] px-2.5 py-1 rounded disabled:opacity-40"><Send size={12} /> Respond</button>
    </div>
  );
}

function DecisionsView({ data, clientId, onChange }) {
  const items = data.items || [];
  const s = data.summary || {};
  const open = ["Proposed", "In review"];
  return (
    <div data-testid="portal-decisions">
      <div className="mb-3">{pill(s.overdue ? "red" : s.outstanding ? "amber" : "green", `Decisions — ${s.outstanding || 0} outstanding · ${s.decided || 0} decided`)}</div>
      {items.length === 0 ? <p className="text-[13px] text-zinc-500 py-4">No decisions logged.</p> : (
        <div className="border border-zinc-200 rounded-lg divide-y divide-zinc-100">{items.map((i) => (
          <div key={i.id} className="px-3 py-2.5" data-testid={`portal-decision-${i.id}`}>
            <div className="flex items-start gap-3 flex-wrap">
              <span className={`${td} font-mono text-zinc-400 !px-0 w-24 flex-none`}>{i.ref}</span>
              <div className="flex-1 min-w-[220px]"><div className="text-[13px] text-zinc-800">{i.title}</div>
                <div className="text-[11px] text-zinc-400">Owner: {i.owner} · {i.status}{i.decision ? ` · ${i.decision}` : (i.due_by ? ` · by ${i.due_by}` : "")}</div>
                {open.includes(i.status) && <DecisionRespond clientId={clientId} itemId={i.id} onChange={onChange} />}
              </div>
            </div>
          </div>))}
        </div>
      )}
    </div>
  );
}

function GatesView({ data }) {
  const items = data.items || [];
  const s = data.summary || {};
  return (
    <div data-testid="portal-gates">
      <div className="mb-3">{pill(s.overall, `Stage-gates — ${s.passed || 0} passed · ${s.at_risk || 0} at risk · ${s.overdue || 0} overdue`)}</div>
      {items.length === 0 ? <p className="text-[13px] text-zinc-500 py-4">No gates tracked.</p> : (
        <div className="border border-zinc-200 rounded-lg overflow-hidden"><table className="w-full">
          <thead className="bg-zinc-50"><tr><th className={th}>Gate</th><th className={th}>RAG</th><th className={th}>Status</th><th className={th}>Target</th><th className={th}>Owner</th></tr></thead>
          <tbody className="divide-y divide-zinc-100">{items.map((i) => (
            <tr key={i.id}><td className={td}>{i.title}</td><td className={td}><Rag rag={i.rag} /></td><td className={td}>{i.status}</td>
              <td className={td}>{i.target_date || "—"}</td><td className={td}>{i.owner}</td></tr>))}
          </tbody></table></div>
      )}
    </div>
  );
}

function LogActual({ clientId, itemId, onChange }) {
  const [f, setF] = useState({ period: "", value: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.value.trim() && !f.period.trim()) return;
    setBusy(true);
    try { await api.post(`/portal/engagement/${clientId}/benefits/${itemId}/actual`, f); toast.success("Actual logged"); setF({ period: "", value: "" }); onChange?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-1.5">
      <input data-testid={`portal-actual-period-${itemId}`} className={`${win} w-16`} placeholder="Q1" value={f.period} onChange={(e) => setF({ ...f, period: e.target.value })} />
      <input data-testid={`portal-actual-value-${itemId}`} className={`${win} w-20`} placeholder="value" value={f.value} onChange={(e) => setF({ ...f, value: e.target.value })} />
      <button data-testid={`portal-actual-submit-${itemId}`} disabled={busy} onClick={submit} className="text-[10px] tracking-[0.08em] uppercase text-[var(--e360-primary,#0B5FFF)] border border-[var(--e360-primary,#0B5FFF)] px-2 py-1 rounded disabled:opacity-40">Log</button>
    </div>
  );
}

function BenefitsView({ data, clientId, onChange }) {
  const items = data.items || [];
  const s = data.summary || {};
  return (
    <div data-testid="portal-benefits">
      <div className="mb-3">{pill(s.overall, `Benefits — ${s.realised || 0} realised · ${s.at_risk || 0} at risk · ${s.behind || 0} behind`)}</div>
      {items.length === 0 ? <p className="text-[13px] text-zinc-500 py-4">No benefits tracked.</p> : (
        <div className="border border-zinc-200 rounded-lg overflow-hidden"><table className="w-full">
          <thead className="bg-zinc-50"><tr><th className={th}>Benefit</th><th className={th}>Baseline</th><th className={th}>Target</th><th className={th}>Latest</th><th className={th}>RAG</th><th className={th}>Log actual</th></tr></thead>
          <tbody className="divide-y divide-zinc-100">{items.map((i) => (
            <tr key={i.id}><td className={td}>{i.benefit}<div className="text-[11px] text-zinc-400">{i.owner}</div></td><td className={td}>{i.baseline}</td>
              <td className={td}>{i.target}</td><td className={`${td} font-medium`}>{i.latest || "—"}</td><td className={td}><Rag rag={i.rag} /></td>
              <td className={td}><LogActual clientId={clientId} itemId={i.id} onChange={onChange} /></td></tr>))}
          </tbody></table></div>
      )}
    </div>
  );
}

const TABS = [{ id: "raid", label: "RAID", icon: ListChecks }, { id: "decisions", label: "Decisions", icon: Gavel },
  { id: "gates", label: "Stage-gates", icon: Flag }, { id: "benefits", label: "Benefits", icon: TrendingUp },
  { id: "documents", label: "Documents", icon: FileText }];

const fmtBytes = (b) => {
  if (!b && b !== 0) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1048576) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1048576).toFixed(1)} MB`;
};
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }); } catch { return ""; } };

function DocumentsView({ clientId }) {
  const [docs, setDocs] = useState(null);
  const [cat, setCat] = useState("all");
  const load = useCallback(() => {
    api.get(`/portal/engagement/${clientId}/documents`).then(({ data }) => setDocs(data.documents || [])).catch(() => setDocs([]));
  }, [clientId]);
  useEffect(() => { load(); }, [load]);
  const acknowledge = async (d) => {
    try { await api.post(`/portal/engagement/${clientId}/documents/${d.id}/acknowledge`); toast.success("Receipt acknowledged"); load(); }
    catch { toast.error("Could not acknowledge"); }
  };
  if (docs === null) return <p className="text-sm text-zinc-400 py-10 text-center">Loading…</p>;
  if (!docs.length) return (
    <div data-testid="portal-documents-empty" className="border border-dashed border-zinc-300 rounded-lg py-12 text-center">
      <FileText size={26} className="mx-auto text-zinc-300 mb-2" />
      <p className="text-[13px] text-zinc-500">No documents have been shared with you yet.</p>
      <p className="text-[12px] text-zinc-400 mt-1">Your engagement team will publish deliverables here — you'll get an email when they do.</p>
    </div>
  );
  const cats = Array.from(new Set(docs.map((d) => d.category).filter(Boolean)));
  const shown = cat === "all" ? docs : docs.filter((d) => d.category === cat);
  return (
    <div className="space-y-2.5" data-testid="portal-documents-list">
      {cats.length > 0 && (
        <div className="flex items-center gap-2 mb-1">
          <span className="text-[11px] uppercase tracking-[0.1em] text-zinc-400">Filter</span>
          <select data-testid="portal-documents-filter" value={cat} onChange={(e) => setCat(e.target.value)}
            className="border border-zinc-300 rounded px-2 py-1 text-[12px]">
            <option value="all">All categories</option>
            {cats.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      )}
      {shown.map((d) => (
        <div key={d.id} data-testid={`portal-document-${d.id}`} className="flex items-center justify-between gap-4 border border-zinc-200 rounded-lg px-4 py-3 bg-white hover:border-[var(--e360-primary,#0B5FFF)] transition-colors">
          <div className="flex items-start gap-3 min-w-0">
            <FileText size={18} className="text-[var(--e360-primary,#0B5FFF)] shrink-0 mt-0.5" />
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-[14px] font-semibold text-zinc-900 truncate">{d.title}</span>
                {d.is_new
                  ? <span className="text-[9px] font-bold uppercase tracking-[0.08em] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded" data-testid={`portal-document-new-${d.id}`}>New</span>
                  : d.version > 1 && <span className="text-[9px] font-bold uppercase tracking-[0.08em] bg-[var(--e360-primary,#0B5FFF)]/10 text-[var(--e360-primary,#0B5FFF)] px-1.5 py-0.5 rounded" data-testid={`portal-document-updated-${d.id}`}>Updated</span>}
              </div>
              <div className="text-[11px] text-zinc-400 flex flex-wrap gap-x-2 gap-y-0.5 mt-0.5">
                {d.category && <span className="text-[var(--e360-primary,#0B5FFF)] font-medium">{d.category}</span>}
                <span>{d.filename}</span><span>·</span><span>{fmtBytes(d.size)}</span>
                {d.published_at && <><span>·</span><span>Published {fmtDate(d.published_at)}</span></>}
              </div>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1.5 shrink-0">
            <a data-testid={`portal-document-download-${d.id}`} href={`${BASE}/api/portal/engagement/${clientId}/documents/${d.id}/download`}
              className="inline-flex items-center gap-1.5 text-[12px] tracking-[0.1em] uppercase text-white bg-[var(--e360-primary,#0B5FFF)] px-3 py-1.5 rounded"><Download size={13} /> Download</a>
            {d.requires_ack && (d.acknowledged
              ? <span data-testid={`portal-document-acknowledged-${d.id}`} className="inline-flex items-center gap-1 text-[11px] text-emerald-700"><CheckCircle2 size={13} /> Acknowledged{d.acknowledged_at ? ` ${fmtDate(d.acknowledged_at)}` : ""}</span>
              : <button data-testid={`portal-document-acknowledge-${d.id}`} onClick={() => acknowledge(d)}
                  className="inline-flex items-center gap-1 text-[11px] font-semibold text-[var(--e360-primary,#0B5FFF)] border border-[var(--e360-primary,#0B5FFF)] px-2.5 py-1 rounded hover:bg-[var(--e360-primary,#0B5FFF)] hover:text-white transition-colors"><CheckCircle2 size={13} /> Acknowledge receipt</button>)}
          </div>
        </div>
      ))}
    </div>
  );
}

function Dashboard({ me }) {
  const [clientId, setClientId] = useState(me.engagements[0]?.client_id);
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("raid");
  const [loading, setLoading] = useState(true);
  const [cert, setCert] = useState(null);
  const eng = me.engagements.find((e) => e.client_id === clientId);
  const showGraph = !!me.features?.evidence_graph;
  const tabs = showGraph ? [...TABS, { id: "evidence", label: "Evidence map", icon: Network }] : TABS;
  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    setCert(null);
    api.get(`/portal/engagement/${clientId}/assurance-certificate`).then(({ data }) => setCert(data)).catch(() => {});
    try { const { data } = await api.get(`/portal/engagement/${clientId}`); setData(data); }
    finally { setLoading(false); }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);
  const signOut = async () => { try { await api.post("/portal/logout"); } finally { window.location.reload(); } };

  return (
    <div className="min-h-screen bg-zinc-50">
      <header className="bg-white border-b border-zinc-200">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-[var(--e360-primary,#0B5FFF)]"><ShieldCheck size={18} /><span className="text-[11px] tracking-[0.18em] uppercase">Delivery &amp; Assurance</span></div>
          <div className="flex items-center gap-3">
            {me.engagements.length > 1 && (
              <select data-testid="portal-engagement-select" className="border border-zinc-300 rounded px-2 py-1 text-[13px]" value={clientId} onChange={(e) => setClientId(e.target.value)}>
                {me.engagements.map((e) => <option key={e.client_id} value={e.client_id}>{e.client_name}</option>)}
              </select>
            )}
            {clientId && <a data-testid="portal-status-pack" href={`${BASE}/api/portal/engagement/${clientId}/status-pack.pdf`}
              className="inline-flex items-center gap-1.5 text-[12px] tracking-[0.1em] uppercase text-white bg-[var(--e360-primary,#0B5FFF)] px-3 py-1.5 rounded"><FileDown size={13} /> Status pack</a>}
            <button data-testid="portal-signout" onClick={signOut} className="inline-flex items-center gap-1.5 text-[12px] text-zinc-500 hover:text-black"><LogOut size={14} /> Sign out</button>
          </div>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-6 py-8" data-testid="portal-dashboard">
        <p className="text-[11px] tracking-[0.14em] uppercase text-zinc-400">{eng?.client_name}</p>
        <h1 className="text-3xl font-semibold text-zinc-900 mb-1">Programme status</h1>
        <p className="text-[13px] text-zinc-500 mb-6">A live view of your reinvention programme — refreshed as your engagement team updates it.</p>
        {cert && (
          <div data-testid="portal-assurance-certificate" className="rounded-lg p-4 mb-6 border border-[var(--e360-primary,#0B5FFF)]/30 bg-[var(--e360-primary,#0B5FFF)]/[0.04]">
            <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
              <span className="text-[11px] tracking-[0.16em] uppercase text-[var(--e360-primary,#0B5FFF)] font-semibold inline-flex items-center gap-1.5"><Award size={14} /> Assurance certificate</span>
              <a data-testid="portal-cert-pdf" href={`${BASE}/api/portal/engagement/${clientId}/assurance-certificate.pdf`}
                className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.1em] uppercase text-[var(--e360-primary,#0B5FFF)] border border-[var(--e360-primary,#0B5FFF)] px-2.5 py-1 rounded"><FileDown size={12} /> Download</a>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-[13px]">
              <div><div className="text-[10px] text-zinc-400 uppercase tracking-[0.1em]">Assurance mode</div><div className="font-semibold capitalize text-zinc-800">{cert.policy.mode}</div></div>
              <div><div className="text-[10px] text-zinc-400 uppercase tracking-[0.1em]">Model-card governance</div><div className="font-semibold text-zinc-800">{cert.gate.tier1_complete}/{cert.gate.tier1_total} complete</div></div>
              <div><div className="text-[10px] text-zinc-400 uppercase tracking-[0.1em]">Evidence integrity</div><div className={`font-semibold ${cert.chain.valid ? "text-emerald-700" : "text-red-700"}`}>{cert.chain.valid ? "Chain verified" : "Unverified"}</div></div>
              <div><div className="text-[10px] text-zinc-400 uppercase tracking-[0.1em]">Model-risk status</div><div className={`font-semibold uppercase ${cert.monitor.state === "suspend" ? "text-red-700" : cert.monitor.state === "watch" ? "text-amber-700" : cert.monitor.state === "active" ? "text-emerald-700" : "text-zinc-600"}`}>{cert.monitor.state}</div></div>
              {cert.model_risk?.score != null && (
                <div data-testid="portal-cert-posture"><div className="text-[10px] text-zinc-400 uppercase tracking-[0.1em]">Board model-risk posture</div><div className={`font-semibold uppercase ${cert.model_risk.rag === "red" ? "text-red-700" : cert.model_risk.rag === "amber" ? "text-amber-700" : "text-emerald-700"}`}>{cert.model_risk.score}/100 {cert.model_risk.rag}</div></div>
              )}
            </div>
            <p className="text-[10px] text-zinc-400 mt-2">Produced under the Ekselens Model Risk Management Framework v1.0. An assurance &amp; readiness view — not a certification or legal opinion.</p>
          </div>
        )}
        <div className="flex gap-1 border-b border-zinc-200 mb-5 flex-wrap">
          {tabs.map((t) => (
            <button key={t.id} data-testid={`portal-tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-1.5 px-4 py-2 text-[12px] tracking-[0.12em] uppercase border-b-2 -mb-px ${tab === t.id ? "border-[var(--e360-primary,#0B5FFF)] text-black" : "border-transparent text-zinc-400"}`}>
              <t.icon size={14} /> {t.label}
              {t.id === "documents" && data?.documents_unread > 0 && (
                <span data-testid="portal-documents-unread-dot" className="ml-1 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full bg-[var(--e360-primary,#0B5FFF)] text-white text-[10px] font-bold leading-none">{data.documents_unread}</span>
              )}
            </button>))}
        </div>
        {loading || !data ? <p className="text-sm text-zinc-400 py-10 text-center">Loading…</p> : (
          <>
            {tab === "raid" && <RaidView data={data.raid} clientId={clientId} onChange={load} />}
            {tab === "decisions" && <DecisionsView data={data.decisions} clientId={clientId} onChange={load} />}
            {tab === "gates" && <GatesView data={data.gates} />}
            {tab === "benefits" && <BenefitsView data={data.benefits} clientId={clientId} onChange={load} />}
            {tab === "documents" && <DocumentsView clientId={clientId} />}
            {tab === "evidence" && showGraph && <EvidenceGraph clientId={clientId} apiBase={`/portal/engagement/${clientId}`} />}
          </>
        )}
      </main>
    </div>
  );
}

export default function ClientPortal() {
  const [me, setMe] = useState(undefined); // undefined=loading, null=signed out
  useEffect(() => {
    api.get("/portal/me").then(({ data }) => setMe(data)).catch(() => setMe(null));
  }, []);
  if (me === undefined) return <div className="min-h-screen flex items-center justify-center text-zinc-400">Loading…</div>;
  if (!me || !me.engagements?.length) return <SignIn />;
  return <Dashboard me={me} />;
}
